import { FC, useEffect } from 'react';

import { StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { Stack, Text } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SummaryStepRendering } from '@sitecore/types/SummaryStep';

import {
  TermsAndConditions,
  SummaryContractNotifications,
  SummaryPaymentDetails,
  SummaryPersonalDetailsOverview,
  SummaryProductsOverview,
  SummaryStepError,
} from '../../../shared/components';
import { useSummaryStepLogic } from '../../../shared/hooks';
import { PriceDetailsOverview } from '../../components';

interface FormFields {
  iban?: string;
}

export const SummaryStep: FC = () => {
  const { SummaryStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SummaryStep: SummaryStepRendering;
  }>();

  const {
    label,
    flowContext,
    sendToFlowMachine,
    offerState,
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    isOrderFailed,
    selectedOffer,
    selectedOfferTermsAndConditions,
    selectedCashbackTexts,
    textualDataKey,
    form,
  } = useSummaryStepLogic<FormFields>({
    extensionMode: 'never',
    paymentDetailsTextualData: textualData?.paymentDetails,
    promotionTexts: textualData?.promotionTexts,
    requireSelectedOfferForSuccess: false,
    includeOrderedOfferInNavigation: true,
  });

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitSuccessful },
  } = form;

  const { isPersonalDetailsEditProhibited, discountCode } = flowContext;

  useEffect(() => {
    // For heat only products, the offer step is skipped which causes the discount code not to be set
    // In this case we need to add the discount code to the flow context manually
    if (offerState.matches('SUCCESS') && !discountCode) {
      sendToFlowMachine({
        type: 'UPDATE_VALUES',
        values: {
          discountCode: offerState.context.offer?.productCombinations?.[0]?.discountCode,
        },
      });
    }
  }, [offerState, discountCode, sendToFlowMachine]);

  return (
    <Layout.Content variant="B" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <StepHeader textualData={textualData} />
          <SummaryContractNotifications selectedOffer={selectedOffer} notifications={textualData?.notifications} />

          <Stack.Item>
            <SummaryPersonalDetailsOverview
              textualData={textualData?.personalDetailsOverview}
              flowContext={flowContext}
              headingLevel="h2"
              onEditPersonalDetails={() => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_PERSONAL_DETAILS' })}
              onEditAddress={() => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_ADDRESS' })}
              onEditMoving={() => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_MOVING' })}
            />
          </Stack.Item>

          <Stack.Item>
            {selectedOffer && (
              <SummaryProductsOverview
                textualData={textualData}
                offer={selectedOffer}
                flowContext={flowContext}
                textualDataKey={textualDataKey}
                selectedCashbackTexts={selectedCashbackTexts}
                cashbackValue={Number(selectedOffer?.costDiscounts?.cashback?.value ?? 0)}
                cashbackType={selectedOffer?.costDiscounts?.cashback?.type}
                priceDetails={
                  <PriceDetailsOverview flowContext={flowContext} textualData={textualData} offer={selectedOffer} />
                }
              />
            )}
          </Stack.Item>

          {!isPersonalDetailsEditProhibited && (
            <SummaryPaymentDetails
              textualData={textualData?.paymentDetails}
              register={register}
              ibanError={errors.iban}
            />
          )}

          <TermsAndConditions
            label={label}
            flowContext={flowContext}
            textualData={textualData}
            offer={selectedOffer}
            offerTermsAndConditions={selectedOfferTermsAndConditions}
          />

          <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>

          {textualData?.content?.energyDisputeCommitteeText?.value && (
            <Text size="BodyXS" color="textLowEmphasis">
              {textualData?.content?.energyDisputeCommitteeText?.value}
            </Text>
          )}
        </>
      )}

      {(isFailed || isOrderFailed) && (
        <SummaryStepError
          isNotAuthenticated={isNotAuthenticated}
          isNotAvailable={isNotAvailable}
          isNotFound={isNotFound}
          isOrderFailed={isOrderFailed}
          textualData={textualData?.genericError}
        />
      )}
    </Layout.Content>
  );
};
